import { DateTime } from "luxon";
import type { PayloadRecord } from "./types";

export const safeParseJson = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isRecordArray = (value: unknown): value is PayloadRecord[] =>
  Array.isArray(value) && value.every((item) => isRecord(item));

export const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((item) => typeof item === "string");

const formatIsoDateTime = (raw: string): string | null => {
  if (!raw.includes("T")) return null;

  const dt = DateTime.fromISO(raw, { setZone: true });
  if (!dt.isValid) return null;

  const date = dt.toFormat("dd/MM/yyyy");
  const time = dt.toFormat("hh:mm a");

  return `${date}, ${time}`;
};

const formatDateTimeRangeRecord = (value: Record<string, unknown>): string | null => {
  const start = typeof value.startDateTime === "string" ? value.startDateTime : undefined;
  const end = typeof value.endDateTime === "string" ? value.endDateTime : undefined;
  if (!start && !end) return null;

  const startText = start ? (formatIsoDateTime(start) ?? start) : "-";
  const endText = end ? (formatIsoDateTime(end) ?? end) : "-";
  return `${startText} – ${endText}`;
};

const isCentsMoneyKey = (key?: string): boolean => {
  if (!key) return false;

  const normalizedKey = key.toLowerCase();

  if (normalizedKey.includes("pricingmode")) return false;
  if (normalizedKey.includes("percent") || normalizedKey.includes("percentage")) return false;
  if (normalizedKey.endsWith("cents")) return true;

  // Be conservative: format the most common cents-based fields.
  // (Other numeric fields like duration/sortOrder are handled normally.)
  const centsKeys = new Set([
    "baseprice",
    "totalprice",
    "originalprice",
    "originaltotalprice",
    "discountamount",
    "optionstotal",
    "price",
    "amount",
  ]);

  if (centsKeys.has(normalizedKey)) return true;
  if (normalizedKey.endsWith("price")) return true;
  if (normalizedKey.endsWith("amount")) return true;

  return false;
};

const asFiniteNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const formatCentsAsDecimal = (cents: number): string => {
  const amount = cents / 100;
  return amount.toFixed(2);
};

const PRICING_MODE_LABELS: Record<string, string> = {
  SINGLE_PRICE: "Fixed Price",
  NO_PRICE: "Quote-based",
  MATRIX_PRICE: "Configurable",
};

export const toDisplayValue = (value: unknown, key?: string): string => {
  if (value === null || value === undefined) {
    return "-";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (key === "pricingMode" && typeof value === "string" && value in PRICING_MODE_LABELS) {
    return PRICING_MODE_LABELS[value];
  }

  if (typeof value === "string") {
    const formatted = formatIsoDateTime(value);
    if (formatted) return formatted;
  }

  if (isCentsMoneyKey(key)) {
    const maybeNumber = asFiniteNumber(value);
    if (maybeNumber !== null) {
      return formatCentsAsDecimal(maybeNumber);
    }
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map((item) => toDisplayValue(item)).join(", ") : "-";
  }

  if (isRecord(value)) {
    const range = formatDateTimeRangeRecord(value);
    if (range) return range;
    return JSON.stringify(value);
  }

  return String(value);
};

export const getFieldLabel = (key: string, fieldLabels?: Record<string, string>): string =>
  fieldLabels?.[key] || key;
